// Value-vs-volume contributor quadrants. Single source of truth for the label
// and colour of each quadrant, shared by the contributor scatter, its legend,
// and the ContributorBadge shown on member cards across the admin panel.
//
// Keys are stable identifiers (like member stages); the "Key voice"-themed
// display names live in `label`. A cohesive, blue-anchored categorical palette
// tuned for the light theme — deep enough to stay high-contrast on white, cool
// (blue / violet / teal) for the value roles with one warm ochre for the "noise"
// role and a neutral slate for spectators.
// `newcomer` = too little activity to classify credibly; never plotted on the
// scatter, only shown as a badge on member cards / the members table.
//
// `departed` is the odd one out, and the only key here that isn't a position on the
// value/volume map: it's read off membership status and OVERRIDES whatever the
// activity numbers say, because "how do they contribute" stops having an answer once
// someone leaves. It is terminal — every other role can flow into it, none flows out
// — so it is never plotted on the scatter, and surfaces that show a mix show it as a
// separate figure rather than a sixth slice (see ROLE_ORDER below).
// Its colour is the theme's `--color-error` (#c23b32), the same red the departed
// membership statuses wear, so "gone" reads identically whichever axis you are looking
// at. It was previously the palette's most muted grey — chosen to keep departed members
// from competing for attention — but grey reads as "inactive", not "left", and it sat a
// shade away from `spectator`'s grey, so the terminal state was the quietest thing on a
// chart whose whole point is churn.
export type Quadrant = 'quiet_expert' | 'key_voice' | 'broadcaster' | 'spectator' | 'newcomer' | 'departed';

// `icon` is a Lucide glyph name (rendered by RoleIcon); `tip` is a one-line,
// human-readable explanation of what the role means, shown in an info tooltip.
export type QuadMeta = { label: string; blurb: string; color: string; icon: string; tip: string };

export const QUAD: Record<Quadrant, QuadMeta> = {
  key_voice: {
    label: 'Key voice', blurb: 'high volume & value', color: '#3452c4', icon: 'star',
    tip: 'Your core contributors — they post often and their posts earn above-average upvotes. The people carrying the community.',
  },
  quiet_expert: {
    label: 'Quiet expert', blurb: 'few posts, high value', color: '#7a3fb0', icon: 'gem',
    tip: 'Post rarely, but each post lands — above-average value per contribution. Worth amplifying; leaderboards never surface them.',
  },
  broadcaster: {
    label: 'Broadcaster', blurb: 'lots of posts, low value', color: '#9c6714', icon: 'megaphone',
    tip: 'Post a lot, but each post earns below-average upvotes. High volume, low signal — plenty of noise, little pull.',
  },
  spectator: {
    label: 'Spectator', blurb: 'low volume & value', color: '#616b78', icon: 'eye',
    tip: 'Below average on both volume and value — mostly reading, only occasionally chiming in.',
  },
  newcomer: {
    label: 'Newcomer', blurb: 'too little to place yet', color: '#237b90', icon: 'sprout',
    tip: 'Too little activity so far to place in a quadrant — new or barely active members.',
  },
  departed: {
    label: 'Departed', blurb: 'no longer a member', color: '#c23b32', icon: 'door-open',
    tip: 'No longer a member of this community — they churned, left, or were removed. Everything they posted is kept, and the role journey beside their name still shows what they were while they were here.',
  },
};

export const quadMeta = (q: string | null | undefined): QuadMeta =>
  QUAD[(q ?? '') as Quadrant] ?? QUAD.spectator;

export const isQuadrant = (q: string | null | undefined): q is Quadrant => q != null && q in QUAD;

// Canonical order of the LIVE roles (best → newcomer) for legends and breakdown
// bars. `departed` is deliberately NOT in it: it is terminal, and on a swept
// community it is routinely the single largest bucket (71 vs 10/3/3 on the first
// one), so dropping it into the same 100% bar squashes the mix of people who are
// actually here into a sliver. Every surface that needs it shows it as a separate
// figure beside the bar (RoleBreakdown, ContributorRoles) or as a terminal band
// (RoleFlow, via ROLE_ORDER_ALL) — one rule: departed is shown alongside, never
// inline.
export const ROLE_ORDER: Quadrant[] = ['key_voice', 'quiet_expert', 'broadcaster', 'spectator', 'newcomer'];

// The terminal role — see the type doc above.
export const TERMINAL_ROLE: Quadrant = 'departed';

// Every role including the terminal one, terminal LAST. For the surfaces that must
// enumerate the whole set: the alluvial's bands (so departed stacks at the bottom of
// each column), its legend, and the members-explorer role facet.
export const ROLE_ORDER_ALL: Quadrant[] = [...ROLE_ORDER, TERMINAL_ROLE];

export interface RoleSegment {
  role: Quadrant;
  count: number;
  pct: number;
  label: string;
  color: string;
}

// Turn a {role: count} map into ordered, coloured segments for a breakdown bar.
// `total` sets the denominator for `pct` (e.g. a cohort size); when omitted the
// segments are normalised to sum to 100%. Shared by every role-breakdown surface.
//
// LIVE ROLES ONLY (ROLE_ORDER): the maps handed in now carry a `departed` bucket
// too — a cohort's role mix, a day's activity split and a week's engaged members all
// classify by the role held at the time, and someone who has left carries the
// terminal role from their departure month on. Segmenting it inline would let one
// terminal bucket eat most of the bar on any community with real churn. It is not
// dropped: `departedIn()` hands the caller that count so it can be shown beside the
// bar (RoleBreakdown does), which is also why a `total`-based bar can legitimately
// under-fill — the missing width is exactly the people who left.
export function roleSegments(
  roles: Record<string, number> | null | undefined,
  total?: number,
): RoleSegment[] {
  if (!roles) return [];
  const active = ROLE_ORDER.filter((r) => (roles[r] ?? 0) > 0);
  const denom = (total ?? active.reduce((sum, r) => sum + (roles[r] ?? 0), 0)) || 1;
  return active.map((r) => ({
    role: r,
    count: roles[r] ?? 0,
    pct: (roles[r] ?? 0) / denom,
    label: QUAD[r].label,
    color: QUAD[r].color,
  }));
}

/** The terminal bucket in a {role: count} map — the part `roleSegments()` leaves out
 *  so callers can render it as its own figure. 0 when there is none (an unswept
 *  community has no departure data at all). */
export function departedIn(roles: Record<string, number> | null | undefined): number {
  return roles?.[TERMINAL_ROLE] ?? 0;
}
